import request from 'lib/request';
import {
  atomWithLoadable,
  lazyAtomWithLoadable,
  mutationAtomWithLoadable,
} from 'lib/atoms';
import { atom } from 'jotai';
import { atomWithStorage } from 'jotai/utils';
import { getMapDefaultConfig } from 'components/map/atoms';
import { createMultiFileAtom } from 'lib/atoms/file-atom';
import searchAtomPair from 'atoms/search-atom';
import type { EntitySearchParams } from 'components/entity-search-table';
import type { AfeHeaderDetailResponse } from '../types';
import type {
  EntityWithAccountsResponse,
  WellIntersectionAnalysisResponse,
  WellMapResponse,
} from 'types/api-responses';
import type {
  AfeCategoryLookupRequest,
  AfeEnums,
  BasinOption,
  CountiesResponse,
  CountyOption,
  FormationOption,
  SaveAfeParams,
  StateOption,
  TypeaheadResponse,
  WellFieldLookupRequest,
  WizardFileRow,
} from './types';
import { buildWizardVisibleFiles } from './utils';

// ─────────────────────────────────────
// Self-fetching atoms (fire on mount)
// ─────────────────────────────────────

/**
 * AFE enum values (afeType, wellType, heldFlag, costItemUnit, costCategories).
 * @return {[Atom<Promise<AfeEnums>>, Atom<Loadable<AfeEnums>>]} Base and loadable atoms
 */
export const [afeEnumsAtom, afeEnumsLoadableAtom] = atomWithLoadable(
  async () => {
    const response = await request.get<AfeEnums>('/afe/enums');
    if ('error' in response) {
      throw new Error(response.error);
    }
    return response;
  }
);

/**
 * Basin dropdown options from GET /basins.
 * Lazy — fetches on first trigger (ComboBox focus), not on mount.
 * @return {[Atom<Promise<BasinOption[]>>, Atom<Loadable<BasinOption[]>>]} Base and loadable atoms
 */
export const [basinsAtom, basinsLoadableAtom] = lazyAtomWithLoadable(
  async () => {
    const response = await request.get<{ basins: BasinOption[] }>(
      '/basins?include_custom_afe_basins=true'
    );
    if ('error' in response) {
      throw new Error(response.error);
    }
    return response.basins;
  }
);

/**
 * State dropdown options from GET /states.
 * Lazy — fetches on first trigger (ComboBox focus), not on mount.
 * @return {[Atom<Promise<StateOption[]>>, Atom<Loadable<StateOption[]>>]} Base and loadable atoms
 */
export const [statesAtom, statesLoadableAtom] = lazyAtomWithLoadable(
  async () => {
    const response = await request.get<StateOption[]>('/states');
    if ('error' in response) {
      throw new Error(response.error);
    }
    return response;
  }
);

// ─────────────────────────────────────
// Event-driven dropdown atoms
// ─────────────────────────────────────

/**
 * Formation options filtered by basin. Triggered explicitly on ComboBox focus.
 * @param {string | null} basinName - Basin to filter by, or null for all
 * @return {[Atom, Atom<Loadable<FormationOption[]>>]} Base and loadable atoms
 */
export const [formationsAtom, formationsLoadableAtom] =
  mutationAtomWithLoadable<FormationOption[], [string | null]>(
    async (_get, _set, basinName: string | null) => {
      const params = new URLSearchParams({
        include_custom_afe_formations: 'true',
      });
      if (basinName && basinName !== 'other') {
        params.set('basin_name', basinName);
      }

      const response = await request.get<{ formations: FormationOption[] }>(
        `/formations?${params}`
      );
      if ('error' in response) {
        throw new Error(response.error);
      }
      return response.formations;
    }
  );

/**
 * County options filtered by state. Triggered explicitly on state selection
 * and ComboBox focus.
 * @param {number} stateId - State ID to filter counties by
 * @return {[Atom, Atom<Loadable<CountyOption[]>>]} Base and loadable atoms
 */
export const [countiesAtom, countiesLoadableAtom] = mutationAtomWithLoadable<
  CountyOption[],
  [number]
>(async (_get, _set, stateId: number) => {
  const response = await request.get<CountiesResponse>(
    `/counties?state_id=${stateId}`
  );
  if ('error' in response) {
    throw new Error(response.error);
  }
  return response.results;
});

// ─────────────────────────────────────
// Typeahead atoms (server-driven search)
// ─────────────────────────────────────

/**
 * UWI typeahead — searches wells by UWI via POST /wells/field_lookup.
 * Triggered by debounced user input. The loadable atom provides
 * loading/data/error states for the ComboBox.
 * @param {string} text - Search text entered by the user
 * @return {[Atom, Atom<Loadable<TypeaheadResponse>>]} Base and loadable atoms
 */
export const [uwiTypeaheadAtom, uwiTypeaheadLoadableAtom] =
  mutationAtomWithLoadable<TypeaheadResponse, [string]>(
    async (_get, _set, text: string) => {
      const response = await request.post<
        TypeaheadResponse,
        WellFieldLookupRequest
      >(
        '/wells/field_lookup',
        { field: 'uwi', text, limit: 10 },
        new AbortController()
      );
      if (response.type === 'success') return response.data;
      throw new Error('Failed to search UWI');
    }
  );

/**
 * Operator typeahead — searches operators via POST /wells/field_lookup.
 * Triggered by debounced user input. The loadable atom provides
 * loading/data/error states for the ComboBox.
 * @param {string} text - Search text entered by the user
 * @return {[Atom, Atom<Loadable<TypeaheadResponse>>]} Base and loadable atoms
 */
export const [operatorTypeaheadAtom, operatorTypeaheadLoadableAtom] =
  mutationAtomWithLoadable<TypeaheadResponse, [string]>(
    async (_get, _set, text: string) => {
      const response = await request.post<
        TypeaheadResponse,
        WellFieldLookupRequest
      >(
        '/wells/field_lookup',
        { field: 'operator', text, limit: 10 },
        new AbortController()
      );
      if (response.type === 'success') return response.data;
      throw new Error('Failed to search operator');
    }
  );

// ─────────────────────────────────────
// Cost typeahead atom factories (per-row instances)
// ─────────────────────────────────────

/**
 * Factory that creates a fresh category typeahead atom pair.
 * Each CostRow calls this once (via useMemo) so rows have independent search state.
 * Searches via POST /queries/afe_headers with lookup_type: 'categories'.
 * @param {string} text - Search text entered by the user
 * @return {[Atom, Atom<Loadable<TypeaheadResponse>>]} Base and loadable atoms
 */
export const createCategoryTypeaheadAtoms = () =>
  mutationAtomWithLoadable<TypeaheadResponse, [string]>(
    async (_get, _set, text: string) => {
      const response = await request.post<
        TypeaheadResponse,
        AfeCategoryLookupRequest
      >(
        '/queries/afe_headers',
        { lookup_type: 'categories', text, limit: 10 },
        new AbortController()
      );
      if (response.type === 'success') return response.data;
      throw new Error('Failed to search categories');
    }
  );

/**
 * Factory that creates a fresh sub-category typeahead atom pair.
 * Each CostRow calls this once (via useMemo) so rows have independent search state.
 * Searches via POST /queries/afe_headers with lookup_type: 'subcategories'
 * scoped to the parent category.
 * @param {string} text - Search text entered by the user
 * @param {string} category - Parent category to scope results
 * @return {[Atom, Atom<Loadable<TypeaheadResponse>>]} Base and loadable atoms
 */
export const createSubCategoryTypeaheadAtoms = () =>
  mutationAtomWithLoadable<TypeaheadResponse, [string, string]>(
    async (_get, _set, text: string, category: string) => {
      const response = await request.post<
        TypeaheadResponse,
        AfeCategoryLookupRequest
      >(
        '/queries/afe_headers',
        {
          lookup_type: 'subcategories',
          text,
          limit: 10,
          filters: { category },
        },
        new AbortController()
      );
      if (response.type === 'success') return response.data;
      throw new Error('Failed to search sub-categories');
    }
  );

// ─────────────────────────────────────
// Mutation atoms (fire on explicit trigger)
// ─────────────────────────────────────

/**
 * Fetch existing AFE for edit mode.
 * Uses mutationAtomWithLoadable because the fetch needs an afeId argument
 * (lazyAtomWithLoadable doesn't support args in the read function).
 * @param {string} afeId - The AFE ID to fetch
 * @return {[Atom, Atom<Loadable<AfeHeaderDetailResponse>>]} Base and loadable atoms
 */
export const [afeDetailAtom, afeDetailLoadableAtom] = mutationAtomWithLoadable<
  AfeHeaderDetailResponse,
  [string]
>(async (_get, _set, afeId: string) => {
  const response = await request.get<AfeHeaderDetailResponse>(
    `/afe_headers/${afeId}`
  );
  if ('error' in response) {
    throw new Error('Failed to load AFE');
  }
  return response;
});

/**
 * Save AFE mutation — creates (POST) or updates (PATCH) based on mode.
 * @param {SaveAfeParams} params - Payload, mode, and afeId
 * @return {[Atom, Atom<Loadable<AfeHeaderDetailResponse>>]} Base and loadable atoms
 */
export const [saveAfeAtom, saveAfeLoadableAtom] = mutationAtomWithLoadable<
  AfeHeaderDetailResponse,
  [SaveAfeParams]
>(async (_get, _set, { payload, mode, afeId }: SaveAfeParams) => {
  const url = mode === 'edit' ? `/afe_headers/${afeId}` : '/afe_headers';
  const method = mode === 'edit' ? 'patch' : 'post';
  const response = await request[method]<AfeHeaderDetailResponse, unknown>(
    url,
    payload,
    new AbortController()
  );
  if (response.type === 'success') return response.data;
  throw new Error(
    response.type === 'error' ? 'Validation failed' : 'Failed to save AFE'
  );
});

// ─────────────────────────────────────
// Well map atoms (fire on UWI selection)
// ─────────────────────────────────────

/**
 * Fetch well map data (wellbores + surface hole) for the AFE well map.
 * Triggered when a UWI is selected in the Existing Well form.
 * @param {string} uwi - The UWI to fetch well data for
 * @return {[Atom, Atom<Loadable<WellMapResponse>>]} Base and loadable atoms
 */
export const [wellQueryAtom, wellQueryLoadableAtom] = mutationAtomWithLoadable<
  WellMapResponse,
  [string]
>(async (_get, _set, uwi: string) => {
  const response = await request.post<
    WellMapResponse,
    { uwi: string; includeDeviationSurvey2d: boolean }
  >(
    '/queries/wells',
    { uwi, includeDeviationSurvey2d: true },
    new AbortController()
  );
  if (response.type === 'success') return response.data;
  throw new Error('Failed to load well map data');
});

/**
 * Fetch well intersection analysis (unit polygons) for the AFE well map.
 * Triggered when a UWI is selected in the Existing Well form.
 * @param {string} uwi - The UWI to fetch intersection data for
 * @return {[Atom, Atom<Loadable<WellIntersectionAnalysisResponse>>]} Base and loadable atoms
 */
export const [afeWellIntersectionAtom, afeWellIntersectionLoadableAtom] =
  mutationAtomWithLoadable<WellIntersectionAnalysisResponse, [string]>(
    async (_get, _set, uwi: string) => {
      const response = await request.post<
        WellIntersectionAnalysisResponse,
        { query: { well_uwi: string } }
      >(
        '/queries/well_intersection_analysis',
        { query: { well_uwi: uwi } },
        new AbortController()
      );
      if (response.type === 'success') return response.data;
      throw new Error('Failed to load well intersection data');
    }
  );

/**
 * Map config atom for the AFE well map, persisted to localStorage.
 * All built-in layers disabled — only manually-rendered children
 * (unit polygons, surface hole) appear on the map.
 */
export const afeMapConfigAtom = atomWithStorage(
  'insights-afe-well-map-config-v2',
  getMapDefaultConfig({
    wells: {
      checked: false,
      options: {
        surfaceHole: true,
        bottomHole: true,
        stick: true,
        survey: true,
        vertical: false,
      },
    },
  })
);

// ─────────────────────────────────────
// Entity search (searchAtomPair)
// ─────────────────────────────────────

/**
 * Entity search for the AFE wizard entity step.
 * Uses searchAtomPair to POST /queries/entities with text search.
 * Independent instance from CRM entity search — searches don't interfere.
 * @return {{ searchAtom, resetSearchDataAtom }} Search atom pair
 */
export const afeEntitySearchAtom = searchAtomPair<
  EntityWithAccountsResponse,
  EntitySearchParams
>('entities');

// ─────────────────────────────────────
// Story 2 — Multi-document upload state (wizard)
// ─────────────────────────────────────

/**
 * In-flight + completed new file uploads for the Supporting Information
 * step. List entries with `hidden: true` are filtered from the visible
 * list and from the save payload, so per-row Delete on a new upload can
 * be undone-on-paper (the entry stays in state with the hidden flag set).
 *
 * Module-singleton state — the wizard's mount component must reset this
 * on entry/unmount so selections from one AFE edit don't leak into the
 * next mount for a different AFE.
 */
export const wizardNewUploadsAtom = createMultiFileAtom();

/**
 * Existing-file ids the user has marked for deletion on Save (edit mode).
 * Hidden from the visible list on read; emitted as `{ id, _destroy: true }`
 * entries in `supporting_files_attributes` when the wizard saves.
 *
 * Defers the destroy until Save — distinct from the detail panel's
 * `localDeletedIds` Set, which fires the destroy immediately and is purely
 * an optimistic UI hide. The wizard equivalent must hold the intent so
 * the user can still Cancel without firing any network call.
 *
 * Writers must produce a NEW Set instance — Jotai compares with
 * `Object.is`, so in-place `set.add(id)` is invisible. Use
 * `setPendingDeletes((prev) => new Set(prev).add(id))`.
 *
 * Module-singleton state — same reset-on-mount caveat as
 * {@link wizardNewUploadsAtom}.
 */
export const wizardPendingDeletesAtom = atom<Set<number>>(new Set<number>());

/**
 * Read-only derived atom: the files visible in the wizard after applying
 * pending deletes and `hidden` flags. Both the Supporting Information
 * step and the Summary preview read from this atom so both surfaces
 * render the same list. The derivation logic lives in
 * {@link buildWizardVisibleFiles} (utils.ts).
 */
export const wizardVisibleFilesAtom = atom<WizardFileRow[]>((get) => {
  const detail = get(afeDetailLoadableAtom);
  const newUploads = get(wizardNewUploadsAtom);
  const pendingDeletes = get(wizardPendingDeletesAtom);

  const existing =
    detail.state === 'hasData' && detail.data
      ? detail.data.data.supportingFiles
      : [];

  return buildWizardVisibleFiles(existing, newUploads, pendingDeletes);
});
